import lightGallery from "lightgallery";
import type { LightGallery as ILightGallery } from "lightgallery/lightgallery";
import lgThumbnail from "lightgallery/plugins/thumbnail";
import lgZoom from "lightgallery/plugins/zoom";
import { useEffect, useMemo, useRef } from "react";
import "lightgallery/css/lightgallery.css";
import "lightgallery/css/lg-thumbnail.css";
import "lightgallery/css/lg-zoom.css";
import type { GbpMediaItem } from "@/gql/graphql";

interface GbpMediaGalleryDialogProps {
  media: GbpMediaItem[];
  onInit: (instance: ILightGallery | null) => void;
}

export function GbpMediaGalleryDialog({ media, onInit }: GbpMediaGalleryDialogProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const lgRef = useRef<ILightGallery | null>(null);
  const onInitRef = useRef(onInit);
  onInitRef.current = onInit;

  const dynamicEl = useMemo(
    () =>
      media
        .filter((item) => item.googleUrl || item.thumbnailUrl)
        .map((item) => ({
          src: item.thumbnailUrl ?? item.googleUrl ?? "",
          thumb: item.thumbnailUrl ?? item.googleUrl ?? "",
        })),
    [media]
  );

  useEffect(() => {
    if (!containerRef.current || dynamicEl.length === 0) return;

    const lg = lightGallery(containerRef.current, {
      dynamic: true,
      dynamicEl,
      plugins: [lgZoom, lgThumbnail],
      licenseKey: "0000-0000-000-0000",
      speed: 500,
      download: false,
    });

    lgRef.current = lg;
    onInitRef.current(lg);

    return () => {
      lg.destroy();
      lgRef.current = null;
      onInitRef.current(null);
    };
  }, [dynamicEl]);

  if (dynamicEl.length === 0) return null;

  return <div ref={containerRef} className="hidden" aria-hidden="true" />;
}
